/* eslint-disable */

import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";

import MDTypography from "components/MDTypography";
import { moneyFmtDynamic, nfDynamic } from "utils/formatters";
import { getMachineBaseName } from "utils/machineDisplayName";
import { getAccumulationModeLabel } from "utils/accumulationModeLabels";

const DATE_COLUMN_WIDTH = 140;
const MACHINE_COLUMN_MIN_WIDTH = 190;
const MACHINE_HEADER_HEIGHT = 112;
const VALUE_HEADER_HEIGHT = 34;
const GRID_BG_EVEN = "#ffffff";
const GRID_BG_ODD = "#f3f6fa";
const GRID_COLUMN_EVEN = "#ffffff";
const GRID_COLUMN_ODD = "#eef3f8";
const GRID_HOVER_ROW = "#eaf3ff";
const GRID_HOVER_COLUMN = "#e6f0ff";
const GRID_HOVER_INTERSECTION = "#dcecff";
const INITIAL_VALUES_COLOR = "#1E5A8A";
const POST_RESET_COLOR = "#8B1E1E";


const paletteByKind = {
  collection: {
    backgroundColor: "#ffffff",
    accentColor: "#1a73e8",
    mutedColor: "#6b7280",
    borderColor: "rgba(26, 115, 232, 0.12)",
  },
  not_collected: {
    backgroundColor: "#ffffff",
    accentColor: "#6b7280",
    mutedColor: "#6b7280",
    borderColor: "rgba(107, 114, 128, 0.16)",
  },
  initialValues: {
    backgroundColor: "#eef6ff",
    accentColor: "#1565c0",
    mutedColor: "#54718f",
    borderColor: "rgba(21, 101, 192, 0.18)",
  },
  reset: {
    backgroundColor: "#fff1e8",
    accentColor: "#d96c1f",
    mutedColor: "#9c5a2d",
    borderColor: "rgba(217, 108, 31, 0.2)",
  },
  empty: {
    backgroundColor: "#f8fafc",
    accentColor: "#98a2b3",
    mutedColor: "#98a2b3",
    borderColor: "rgba(152, 162, 179, 0.18)",
  },
  informative: {
    backgroundColor: "#f8fafc",
    accentColor: "#7b8794",
    mutedColor: "#7b8794",
    borderColor: "rgba(123, 135, 148, 0.18)",
  },
};

function getMachineColumnKey(machine) {
  if (!machine?.columnKey) return null;
  const key = String(machine.columnKey).trim();
  return key || null;
}

function buildColumnKey({ machineId, installationId, localMachineId }) {
  const normalizedMachineId = machineId == null ? null : Number(machineId);
  const normalizedInstallationId = installationId == null ? null : Number(installationId);
  const normalizedLocalMachineId = localMachineId == null ? null : Number(localMachineId);

  if (!Number.isFinite(normalizedMachineId)) return null;
  if (Number.isFinite(normalizedInstallationId)) {
    return `machine:${normalizedMachineId}:installation:${normalizedInstallationId}`;
  }
  if (Number.isFinite(normalizedLocalMachineId)) {
    return `machine:${normalizedMachineId}:localMachine:${normalizedLocalMachineId}`;
  }
  return null;
}

function getCellForMachine(row, machine) {
  const cells = row?.cells;
  const machineColumnKey = getMachineColumnKey(machine);

  if (!cells || !machineColumnKey) return null;
  if (cells[machineColumnKey]) return cells[machineColumnKey];

  const matchingKey = Object.keys(cells).find(
    (cellKey) => String(cellKey).trim() === machineColumnKey
  );

  return matchingKey ? cells[matchingKey] : null;
}

function getCellForColumnKey(row, columnKey) {
  const cells = row?.cells;
  const normalizedColumnKey = columnKey == null ? null : String(columnKey).trim();

  if (!cells || !normalizedColumnKey) return null;
  if (cells[normalizedColumnKey]) return cells[normalizedColumnKey];

  const matchingKey = Object.keys(cells).find(
    (cellKey) => String(cellKey).trim() === normalizedColumnKey
  );

  return matchingKey ? cells[matchingKey] : null;
}

function toPositiveIntegerOrNull(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function appendDisplayNumberTransition(history, value) {
  const parsed = toPositiveIntegerOrNull(value);
  if (parsed == null) return history;
  if (history[history.length - 1] !== parsed) history.push(parsed);
  return history;
}

function normalizeRepeatedHistorySequence(values = []) {
  const history = [];

  (Array.isArray(values) ? values : []).forEach((value) => {
    appendDisplayNumberTransition(history, value);
  });

  for (let size = 1; size <= Math.floor(history.length / 2); size += 1) {
    if (history.length % size !== 0) continue;
    let repeats = true;
    for (let index = size; index < history.length; index += 1) {
      if (history[index] !== history[index % size]) {
        repeats = false;
        break;
      }
    }
    if (repeats) return history.slice(0, size);
  }

  return history;
}

function getColumnLocalDisplayNumberSummary(machine, rows = []) {
  const machineColumnKey = getMachineColumnKey(machine);
  const history = [];

  (machine?.localDisplayNumberHistory || machine?.LocalDisplayNumberHistory || []).forEach((value) => {
    appendDisplayNumberTransition(history, value);
  });

  if (machineColumnKey) {
    (rows || []).forEach((row) => {
      const cell = getCellForMachine(row, machine);
      appendDisplayNumberTransition(
        history,
        cell?.localDisplayNumber ?? cell?.LocalDisplayNumber
      );
    });
  }

  appendDisplayNumberTransition(
    history,
    machine?.currentLocalDisplayNumber ??
      machine?.localDisplayNumber ??
      machine?.LocalDisplayNumber
  );

  const normalizedHistory = normalizeRepeatedHistorySequence(history);
  const current = normalizedHistory.length ? normalizedHistory[normalizedHistory.length - 1] : null;

  return {
    current,
    history: normalizedHistory,
    historyLabel: normalizedHistory.length > 1 ? `Hist: ${normalizedHistory.join(" → ")}` : null,
  };
}

function getMachineSortDisplayNumber(machine) {
  const directNumber = toPositiveIntegerOrNull(machine?.localDisplayNumber ?? machine?.LocalDisplayNumber);
  if (directNumber != null) return directNumber;

  const history = normalizeRepeatedHistorySequence(
    machine?.localDisplayNumberHistory || machine?.LocalDisplayNumberHistory || []
  );
  if (history.length > 0) return history[history.length - 1];

  return toPositiveIntegerOrNull(machine?.currentLocalDisplayNumber);
}

function compareMachineColumns(a, b) {
  if (a?.currentActive === true && b?.currentActive !== true) return -1;
  if (a?.currentActive !== true && b?.currentActive === true) return 1;

  const numberA = getMachineSortDisplayNumber(a);
  const numberB = getMachineSortDisplayNumber(b);
  if (numberA != null && numberB == null) return -1;
  if (numberA == null && numberB != null) return 1;
  if (numberA != null && numberB != null && numberA !== numberB) {
    return numberA - numberB;
  }

  const nameCompare = String(a?.name || "").localeCompare(String(b?.name || ""), "es", {
    sensitivity: "base",
  });
  if (nameCompare !== 0) return nameCompare;

  const serialCompare = String(a?.serialNumber || "").localeCompare(String(b?.serialNumber || ""), "es", {
    sensitivity: "base",
  });
  if (serialCompare !== 0) return serialCompare;

  const machineIdA = Number(a?.machineId);
  const machineIdB = Number(b?.machineId);
  if (Number.isFinite(machineIdA) && Number.isFinite(machineIdB) && machineIdA !== machineIdB) {
    return machineIdA - machineIdB;
  }

  return String(a?.columnKey || "").localeCompare(String(b?.columnKey || ""), "es", {
    sensitivity: "base",
  });
}

function formatNumber(value) {
  if (value == null) return "—";
  return nfDynamic.format(value);
}

function formatMoney(value) {
  if (value == null) return "—";
  return moneyFmtDynamic.format(value);
}

function formatFormula(formula) {
  if (formula == null) return null;

  if (typeof formula === "string" || typeof formula === "number") {
    const raw = String(formula).trim();
    if (!raw) return null;
    return raw.startsWith("/") || raw.startsWith("*") || raw.startsWith("+") || raw.startsWith("-") || raw.startsWith("$")
      ? raw
      : `/${raw}`;
  }

  if (typeof formula === "object") {
    const type = formula.type == null ? "" : String(formula.type).trim();
    const value = formula.value == null ? "" : String(formula.value).trim();

    if (type && value) return `${type}${value}`;

    if (formula.name != null) {
      const name = String(formula.name).trim();
      if (name) return name;
    }
  }

  return null;
}

function formatAccumulation(isAccumulative) {
  return isAccumulative === false ? "Borrado" : null;
}

export function formatSlotMachineDisplayName(machine, localDisplayNumber) {
  const name = getMachineBaseName(machine, machine?.columnKey || "Sin nombre");
  const number = toPositiveIntegerOrNull(localDisplayNumber);
  return number == null ? name : `${number} - ${name}`;
}

function normalizeConfigLabel(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function getFormulaFromSource(source) {
  if (!source) return null;

  const directFormula =
    source.formula ??
    source.Formula ??
    source.machineFormula ??
    source.MachineFormula ??
    null;

  const id =
    source.FormulaId ??
    source.formulaId ??
    source.MachineFormulaId ??
    source.machineFormulaId ??
    directFormula?.Id ??
    directFormula?.id ??
    null;
  const name =
    source.FormulaName ??
    source.formulaName ??
    source.MachineFormulaName ??
    source.machineFormulaName ??
    directFormula?.Name ??
    directFormula?.name ??
    null;
  const type =
    source.FormulaType ??
    source.formulaType ??
    source.MachineFormulaType ??
    source.machineFormulaType ??
    directFormula?.Type ??
    directFormula?.type ??
    null;
  const value =
    source.FormulaValue ??
    source.formulaValue ??
    source.MachineFormulaValue ??
    source.machineFormulaValue ??
    directFormula?.Value ??
    directFormula?.value ??
    null;

  if (id == null && name == null && type == null && value == null) return null;
  return { id, name, type, value };
}

function hasConfigHints(source) {
  if (!source) return false;
  return (
    source.valueMode != null ||
    source.ValueMode != null ||
    source.isAccumulative != null ||
    source.IsAccumulative != null ||
    source.FormulaId != null ||
    source.formulaId != null ||
    source.MachineFormulaId != null ||
    source.machineFormulaId != null ||
    source.Formula != null ||
    source.formula != null ||
    source.MachineFormula != null ||
    source.machineFormula != null ||
    source.FormulaType != null ||
    source.formulaType != null ||
    source.FormulaValue != null ||
    source.formulaValue != null ||
    source.FormulaName != null ||
    source.formulaName != null
  );
}

function getSourceValueMode(source) {
  if (!source) return null;
  if (source.valueMode) return source.valueMode;
  if (source.ValueMode) return source.ValueMode;
  if (getFormulaFromSource(source)) return "credits";
  return hasConfigHints(source) ? "money" : null;
}

function getSourceIsAccumulative(source) {
  if (!source) return null;
  const raw = source.isAccumulative ?? source.IsAccumulative ?? null;
  if (raw == null) return null;
  if (typeof raw === "boolean") return raw;
  if (typeof raw === "number") return raw === 1;
  const normalized = String(raw).trim().toLowerCase();
  if (normalized === "true" || normalized === "1" || normalized === "yes") return true;
  if (normalized === "false" || normalized === "0" || normalized === "no") return false;
  return null;
}

export function getConfigLabel(source) {
  if (!source) return null;

  const valueMode = getSourceValueMode(source);
  const formulaLabel = formatFormula(getFormulaFromSource(source));
  const accumulationLabel = formatAccumulation(getSourceIsAccumulative(source));
  if (!valueMode && !formulaLabel && !accumulationLabel) return null;
  const configLabel = [accumulationLabel, formulaLabel, valueMode === "money" ? "$" : null]
    .filter(Boolean)
    .join(" | ");

  return configLabel || null;
}

function getConfigComparisonLabel(source) {
  if (!source) return null;
  const valueMode = getSourceValueMode(source);
  const formulaLabel = formatFormula(getFormulaFromSource(source));
  const accumulation = getSourceIsAccumulative(source);
  const accumulationLabel = accumulation == null ? null : getAccumulationModeLabel(accumulation);
  return [formulaLabel, valueMode === "money" ? "$" : null, accumulationLabel]
    .filter(Boolean)
    .join(" | ") || null;
}

function shouldShowCellConfig(cellConfigLabel, machineCurrentConfigLabel) {
  if (!cellConfigLabel) return false;
  if (!machineCurrentConfigLabel) return true;
  return normalizeConfigLabel(cellConfigLabel) !== normalizeConfigLabel(machineCurrentConfigLabel);
}

function formatCellValue(value, valueMode) {
  if (valueMode === "credits") return formatNumber(value);
  if (valueMode === "money") return formatMoney(value);
  return formatNumber(value);
}

function getComparableTime(value) {
  if (!value) return Number.POSITIVE_INFINITY;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
}

function getColumnBackground(machineIndex, isHeader = false, currentActive = true) {
  if (isHeader) {
    if (currentActive !== true) return "#e8edf3";
    return machineIndex % 2 === 0 ? "#f5f7fa" : "#eaf0f6";
  }

  return machineIndex % 2 === 0 ? GRID_COLUMN_EVEN : GRID_COLUMN_ODD;
}

function getCellOuterBackground({ machineIndex, isRowHovered, isColumnHovered, isIntersectionHovered }) {
  if (isIntersectionHovered) return GRID_HOVER_INTERSECTION;
  if (isRowHovered && isColumnHovered) return GRID_HOVER_INTERSECTION;
  if (isColumnHovered) return GRID_HOVER_COLUMN;
  if (isRowHovered) return GRID_HOVER_ROW;
  return getColumnBackground(machineIndex, false);
}

function formatRowDate(row) {
  const dateLabel = row?.dateLabel ? String(row.dateLabel).trim() : "";
  const cleanedLabel = dateLabel.replace(/\s*reset\s*$/i, "").trim();

  if (!row?.dateTime) return cleanedLabel || dateLabel || "—";

  const parsedDate = new Date(row.dateTime);
  if (Number.isNaN(parsedDate.getTime())) return cleanedLabel || dateLabel || "—";

  const baseLabel = cleanedLabel || `${parsedDate.getDate()}/${parsedDate.getMonth() + 1}`;
  return `${baseLabel}/${parsedDate.getFullYear()}`;
}

function canNavigateToVisitDetail(row, localId) {
  return Boolean(localId) && Boolean(row?.visitId) && (row?.type === "collection" || row?.type === "reset");
}

function getCellStyles(cell, row) {
  if (!cell) return paletteByKind.empty;
  if (row?.type === "initialValues" || cell.state === "initial_values") return paletteByKind.initialValues;
  if (row?.type === "reset" || cell.state === "reset") return paletteByKind.reset;
  if (cell.state === "not_collected") return paletteByKind.not_collected;
  if (
    cell.state === "inserted_same_day" ||
    cell.state === "not_installed_yet" ||
    cell.state === "already_removed" ||
    cell.state === "no_data"
  ) {
    return paletteByKind.informative;
  }
  return paletteByKind.collection;
}

function getHoverStyles({ isRowHovered, isColumnHovered, isIntersectionHovered, mode = "cell" }) {
  if (mode === "header") {
    if (isIntersectionHovered) {
      return {
        backgroundColor: GRID_HOVER_INTERSECTION,
        boxShadow: "inset 0 0 0 2px rgba(26, 115, 232, 0.28), inset 0 -1px 0 rgba(26, 115, 232, 0.12)",
      };
    }

    if (isColumnHovered) {
      return {
        backgroundColor: GRID_HOVER_COLUMN,
        boxShadow: "inset 0 0 0 1px rgba(26, 115, 232, 0.18), inset 0 -1px 0 rgba(26, 115, 232, 0.08)",
      };
    }

    return {};
  }

  if (mode === "date") {
    if (isIntersectionHovered) {
      return {
        backgroundColor: GRID_HOVER_INTERSECTION,
        boxShadow: "inset 0 0 0 2px rgba(26, 115, 232, 0.28)",
      };
    }

    if (isRowHovered) {
      return {
        backgroundColor: GRID_HOVER_ROW,
        boxShadow: "inset 0 0 0 1px rgba(26, 115, 232, 0.22)",
      };
    }

    return {};
  }

  if (isIntersectionHovered) {
    return {
      boxShadow: "inset 0 0 0 2px rgba(26, 115, 232, 0.32)",
      transform: "translateY(-1px)",
    };
  }

  if (isRowHovered && isColumnHovered) {
    return {
      boxShadow: "inset 0 0 0 1px rgba(26, 115, 232, 0.16)",
    };
  }

  if (isRowHovered) {
    return {
      boxShadow: "inset 0 0 0 1px rgba(26, 115, 232, 0.14)",
    };
  }

  if (isColumnHovered) {
    return {
      boxShadow: "inset 0 0 0 1px rgba(26, 115, 232, 0.14)",
    };
  }

  return {};
}

export function getSupplementDisplay(cell, kind, machineCurrentConfigLabel = null) {
  if (!cell) return null;

  return {
    title: kind === "initial" ? "Inicio" : "Reset",
    titleColor: kind === "initial" ? INITIAL_VALUES_COLOR : POST_RESET_COLOR,
    borderColor: kind === "initial"
      ? "rgba(36, 71, 109, 0.25)"
      : "rgba(139, 30, 30, 0.25)",
    valueColor: kind === "initial" ? INITIAL_VALUES_COLOR : POST_RESET_COLOR,
    primaryValue: formatCellValue(cell.postResetStartIn, cell.valueMode),
    secondaryValue: formatCellValue(cell.postResetStartOut, cell.valueMode),
    meta: null,
  };
}

export function getCellDisplayValues(
  cell,
  machineCurrentConfigLabel = null,
  machineCurrentComparisonLabel = machineCurrentConfigLabel
) {
  if (!cell || cell.state !== "collected") return null;

  const isMoney = cell.valueMode === "money";
  const isNonAccumulative = cell.isAccumulative === false;
  const configLabel = getConfigLabel(cell);
  const primarySource = isNonAccumulative
    ? isMoney
      ? cell.netMoneyIn
      : cell.netCreditsIn
    : isMoney
      ? cell.moneyIn
      : cell.creditsIn;
  const secondarySource = isNonAccumulative
    ? isMoney
      ? cell.netMoneyOut
      : cell.netCreditsOut
    : isMoney
      ? cell.moneyOut
      : cell.creditsOut;

  return {
    primaryValue: formatCellValue(primarySource, cell.valueMode),
    secondaryValue: formatCellValue(secondarySource, cell.valueMode),
    meta: shouldShowCellConfig(getConfigComparisonLabel(cell), machineCurrentComparisonLabel)
      ? configLabel
      : null,
  };
}

function CellContent({ cell, row, initialCell, currentConfigLabel, currentConfigComparisonLabel }) {
  if (!cell) {
    return (
      <MDTypography
        variant="caption"
        color="text"
        sx={{ opacity: 0.75, lineHeight: 1.4, textAlign: "center" }}
      >
        —
      </MDTypography>
    );
  }

  const styles = getCellStyles(cell, row);

  if (cell.state === "not_collected") {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <MDTypography
          variant="button"
          fontWeight="medium"
          sx={{ color: "#1f2937", lineHeight: 1.4, textAlign: "center" }}
        >
          Sin lectura
        </MDTypography>
      </Box>
    );
  }

  if (
    cell.state === "inserted_same_day" ||
    cell.state === "not_installed_yet" ||
    cell.state === "already_removed" ||
    cell.state === "no_data"
  ) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        {cell.state !== "inserted_same_day" && cell.state !== "not_installed_yet" && (
          <MDTypography
            variant="caption"
            fontWeight="medium"
            sx={{ color: "#7b8794", lineHeight: 1.45, textAlign: "center" }}
          >
            {cell.label || "Sin datos"}
          </MDTypography>
        )}
      </Box>
    );
  }

  const display = getCellDisplayValues(cell, currentConfigLabel, currentConfigComparisonLabel);
  const initialDisplay = initialCell ? getSupplementDisplay(initialCell, "initial", currentConfigLabel) : null;
  const postResetDisplay = cell.hasReset && (cell.postResetStartIn != null || cell.postResetStartOut != null)
    ? getSupplementDisplay(cell, "reset", currentConfigLabel)
    : null;

  if (!display) {
    return (
      <MDTypography
        variant="caption"
        color="text"
        sx={{ opacity: 0.75, lineHeight: 1.4, textAlign: "center" }}
      >
        —
      </MDTypography>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      {initialDisplay ? (
        <Box
          sx={{
            width: "100%",
            mb: 0.85,
            pb: 0.7,
            borderBottom: `1px solid ${initialDisplay.borderColor}`,
            textAlign: "center",
          }}
        >
          <MDTypography
            variant="caption"
            fontWeight="bold"
            sx={{
              display: "block",
              color: initialDisplay.titleColor,
              mb: 0.35,
              lineHeight: 1.25,
              textAlign: "center",
            }}
          >
            {initialDisplay.title}
          </MDTypography>
          <MDTypography
            variant="button"
            fontWeight="medium"
            sx={{
              display: "block",
              color: initialDisplay.valueColor,
              lineHeight: 1.35,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
              textAlign: "center",
            }}
          >
            {initialDisplay.primaryValue} | {initialDisplay.secondaryValue}
          </MDTypography>
          {initialDisplay.meta ? (
            <MDTypography
              variant="caption"
              sx={{
                display: "block",
                color: initialDisplay.valueColor,
                mt: 0.55,
                lineHeight: 1.3,
                textAlign: "center",
              }}
            >
              {initialDisplay.meta}
            </MDTypography>
          ) : null}
        </Box>
      ) : null}

      <MDTypography
        variant="button"
        fontWeight="medium"
        sx={{
          display: "block",
          color: "#1f2937",
          lineHeight: 1.45,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: "100%",
          textAlign: "center",
        }}
      >
        {display.primaryValue}
        {display.secondaryValue
          ? ` | ${display.secondaryValue}`
          : ""}
      </MDTypography>

      {display.meta ? (
        <MDTypography
          variant="caption"
          sx={{
            display: "block",
            color: styles.mutedColor,
            mt: 0.75,
            lineHeight: 1.35,
            textAlign: "center",
          }}
        >
          {display.meta}
        </MDTypography>
      ) : null}

      {postResetDisplay ? (
        <Box
          sx={{
            width: "100%",
            mt: 0.9,
            pt: 0.7,
            borderTop: `1px solid ${postResetDisplay.borderColor}`,
            textAlign: "center",
          }}
        >
          <MDTypography
            variant="caption"
            fontWeight="bold"
            sx={{
              display: "block",
              color: postResetDisplay.titleColor,
              mb: 0.35,
              lineHeight: 1.25,
              textAlign: "center",
            }}
          >
            {postResetDisplay.title}
          </MDTypography>
          <MDTypography
            variant="button"
            fontWeight="medium"
            sx={{
              display: "block",
              color: postResetDisplay.valueColor,
              lineHeight: 1.35,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
              textAlign: "center",
            }}
          >
            {postResetDisplay.primaryValue} | {postResetDisplay.secondaryValue}
          </MDTypography>
          {postResetDisplay.meta ? (
            <MDTypography
              variant="caption"
              sx={{
                display: "block",
                color: postResetDisplay.valueColor,
                mt: 0.55,
                lineHeight: 1.3,
                textAlign: "center",
              }}
            >
              {postResetDisplay.meta}
            </MDTypography>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}

function SlotAccountingGrid({ machines, rows, localId, viewState, initialScroll, activeLocalMachines }) {
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const didRestoreScrollRef = useRef(false);
  const [hoveredRowKey, setHoveredRowKey] = useState(null);
  const [hoveredColumnKey, setHoveredColumnKey] = useState(null);

  const sortedMachines = useMemo(
    () =>
      [...machines].sort(compareMachineColumns),
    [machines]
  );

  const currentConfigByColumnKey = useMemo(() => {
    const byColumnKey = new Map();
    const setIfPresent = (columnKey, source) => {
      const label = getConfigLabel(source);
      if (!columnKey || !label || byColumnKey.has(columnKey)) return;
      byColumnKey.set(columnKey, {
        label,
        comparisonLabel: getConfigComparisonLabel(source),
      });
    };

    (activeLocalMachines || []).forEach((machine) => {
      const columnKey = buildColumnKey({
        machineId: machine?.MachineId ?? machine?.machineId,
        installationId: machine?.InstallationId ?? machine?.installationId,
        localMachineId: machine?.LocalMachineId ?? machine?.Id ?? machine?.currentLocalMachineId,
      });
      setIfPresent(columnKey, machine);
    });

    (machines || []).forEach((machine) => {
      setIfPresent(getMachineColumnKey(machine), machine);
    });

    [...(rows || [])]
      .sort((a, b) => getComparableTime(b?.dateTime) - getComparableTime(a?.dateTime))
      .forEach((row) => {
        Object.entries(row?.cells || {}).forEach(([columnKey, cell]) => {
          setIfPresent(String(columnKey).trim(), cell);
        });
      });

    return byColumnKey;
  }, [activeLocalMachines, machines, rows]);

  const { visibleRows, initialValuesByCellKey } = useMemo(() => {
    const collectionRows = rows.filter((row) => row?.type === "collection");
    const initialRows = [...rows]
      .filter((row) => row?.type === "initialValues")
      .sort((a, b) => getComparableTime(a?.dateTime) - getComparableTime(b?.dateTime));
    const initialCandidatesByColumnKey = new Map();

    initialRows.forEach((row) => {
      Object.keys(row?.cells || {}).forEach((columnKey) => {
        const initialCell = getCellForColumnKey(row, columnKey);
        if (!initialCell || initialCell.state !== "initial_values") return;

        const normalizedColumnKey = String(columnKey).trim();
        const entries = initialCandidatesByColumnKey.get(normalizedColumnKey) || [];
        entries.push({
          sortTime: getComparableTime(row.dateTime),
          cell: initialCell,
        });
        initialCandidatesByColumnKey.set(normalizedColumnKey, entries);
      });
    });

    const mappedInitialValues = {};

    initialCandidatesByColumnKey.forEach((entries, columnKey) => {
      const firstCollectionRow = collectionRows.find((row) => {
        const collectionCell = getCellForColumnKey(row, columnKey);
        return collectionCell?.state === "collected";
      });
      if (!firstCollectionRow) return;

      const firstCollectionTime = getComparableTime(firstCollectionRow?.dateTime);
      const candidateBeforeOrEqual = [...entries]
        .filter((entry) => entry.sortTime <= firstCollectionTime)
        .sort((a, b) => b.sortTime - a.sortTime)[0];
      const fallbackCandidate = [...entries].sort((a, b) => a.sortTime - b.sortTime)[0];
      const selectedCandidate = candidateBeforeOrEqual || fallbackCandidate;

      if (!selectedCandidate) return;

      mappedInitialValues[`${firstCollectionRow.rowKey}::${columnKey}`] = selectedCandidate.cell;
    });

    const sortedVisibleRows = [...collectionRows].sort((a, b) => {
      const dateA = getComparableTime(a?.dateTime);
      const dateB = getComparableTime(b?.dateTime);

      if (dateA !== dateB) return dateB - dateA;

      const visitIdA = Number(a?.visitId ?? 0);
      const visitIdB = Number(b?.visitId ?? 0);
      if (visitIdA !== visitIdB) return visitIdB - visitIdA;

      return String(b?.rowKey ?? "").localeCompare(String(a?.rowKey ?? ""));
    });

    return {
      visibleRows: sortedVisibleRows,
      initialValuesByCellKey: mappedInitialValues,
    };
  }, [rows]);

  const gridTemplateColumns = `${DATE_COLUMN_WIDTH}px repeat(${sortedMachines.length}, ${MACHINE_COLUMN_MIN_WIDTH}px)`;

  const scrollToTop = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollTop = 0;
  };

  const scrollToBottom = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
  };

  const getCurrentGridScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) {
      return { scrollTop: 0, scrollLeft: 0 };
    }

    return {
      scrollTop: el.scrollTop || 0,
      scrollLeft: el.scrollLeft || 0,
    };
  };

  useEffect(() => {
    if (didRestoreScrollRef.current) return;
    if (!initialScroll || !scrollContainerRef.current) return;

    didRestoreScrollRef.current = true;

    requestAnimationFrame(() => {
      const el = scrollContainerRef.current;
      if (!el) return;
      el.scrollTop = Number(initialScroll.scrollTop) || 0;
      el.scrollLeft = Number(initialScroll.scrollLeft) || 0;
    });
  }, [initialScroll, sortedMachines.length, visibleRows.length]);

  const handleDateClick = (row) => {
    if (!canNavigateToVisitDetail(row, localId)) return;
    navigate(`/local/${localId}/recaudaciones/${row.visitId}`, {
      state: {
        returnTo: {
          pathname: `/local/${localId}/recaudaciones/tragamonedas`,
          label: "Volver a recaudaciones",
          viewState: {
            ...(viewState || {}),
            gridScroll: getCurrentGridScroll(),
          },
        },
      },
    });
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        gap={1.5}
        flexWrap="wrap"
        mb={1}
      >
        <Box display="flex" gap={1} flexWrap="wrap" ml="auto">
          <Button size="small" variant="contained" color="info" aria-label="Ir al inicio de la grilla" onClick={scrollToTop}>
            Ir al inicio
          </Button>
          <Button size="small" variant="contained" color="info" aria-label="Ir al final de la grilla" onClick={scrollToBottom}>
            Ir al final
          </Button>
        </Box>
      </Box>

      <Box
        ref={scrollContainerRef}
        sx={{
          maxHeight: "70vh",
          overflow: "auto",
          border: "1px solid rgba(0,0,0,0.08)",
          borderRadius: "14px",
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns,
            minWidth: DATE_COLUMN_WIDTH + sortedMachines.length * MACHINE_COLUMN_MIN_WIDTH,
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              position: "sticky",
              top: 0,
              left: 0,
              zIndex: 40,
              backgroundColor: "#f4f7fb",
              borderRight: "1px solid rgba(52, 71, 103, 0.16)",
              borderBottom: "1px solid rgba(0,0,0,0.08)",
              p: 1.5,
              minHeight: MACHINE_HEADER_HEIGHT,
              height: MACHINE_HEADER_HEIGHT,
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          />

          {sortedMachines.map((machine, machineIndex) => {
            const machineColumnKey = getMachineColumnKey(machine);
            const isColumnHovered = hoveredColumnKey != null && hoveredColumnKey === machineColumnKey;
            const currentConfigLabel = currentConfigByColumnKey.get(machineColumnKey)?.label || null;
            const localDisplayNumberSummary = getColumnLocalDisplayNumberSummary(machine, rows);
            const machineDisplayName = formatSlotMachineDisplayName(
              machine,
              localDisplayNumberSummary.current
            );
            const fullTitle = [
              machineDisplayName || "Máquina",
              localDisplayNumberSummary.historyLabel,
              `Nro. serie: ${machine.serialNumber || "Sin serie"}`,
            ].filter(Boolean).join(" - ");

            return (
              <Box
                key={machine.columnKey || machine.machineId || machine.name}
                title={fullTitle}
                aria-label={`Máquina ${machineDisplayName || machine.columnKey || "Sin nombre"}, Nro. serie ${machine.serialNumber || "Sin serie"}`}
                onMouseEnter={() => setHoveredColumnKey(machineColumnKey)}
                onMouseLeave={() => setHoveredColumnKey((current) => (current === machineColumnKey ? null : current))}
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 30,
                  minHeight: MACHINE_HEADER_HEIGHT,
                  height: MACHINE_HEADER_HEIGHT,
                  backgroundColor: getColumnBackground(machineIndex, true, machine.currentActive),
                  borderRight: "1px solid rgba(52, 71, 103, 0.18)",
                  borderBottom: "1px solid rgba(0,0,0,0.08)",
                  boxShadow: "inset -1px 0 0 rgba(52, 71, 103, 0.10)",
                  p: 1.25,
                  boxSizing: "border-box",
                  overflow: "hidden",
                  opacity: machine.currentActive ? 1 : 0.82,
                  transition: "background-color 120ms ease, box-shadow 120ms ease",
                  ...getHoverStyles({
                    isColumnHovered,
                    isIntersectionHovered: isColumnHovered,
                    mode: "header",
                  }),
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    height: "100%",
                    minWidth: 0,
                  }}
                >
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    sx={{
                      display: "block",
                      width: "100%",
                      color: "#344767",
                      lineHeight: 1.25,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      minWidth: 0,
                      textAlign: "center",
                    }}
                  >
                    {machineDisplayName}
                    {machine.currentActive !== true ? " - inactiva" : ""}
                  </MDTypography>
                  {localDisplayNumberSummary.historyLabel ? (
                    <MDTypography
                      variant="caption"
                      sx={{
                        display: "block",
                        width: "100%",
                        color: "#67748e",
                        mt: 0.2,
                        lineHeight: 1.2,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        textAlign: "center",
                        fontSize: "0.68rem",
                      }}
                    >
                      {localDisplayNumberSummary.historyLabel}
                    </MDTypography>
                  ) : null}
                  <MDTypography
                    variant="caption"
                    sx={{
                      display: "block",
                      width: "100%",
                      color: "#67748e",
                      mt: 0.25,
                      lineHeight: 1.25,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      textAlign: "center",
                    }}
                  >
                    Nro. serie: {machine.serialNumber || "Sin serie"}
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    sx={{
                      display: "block",
                      width: "100%",
                      color: "#67748e",
                      mt: 0.25,
                      lineHeight: 1.25,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      textAlign: "center",
                    }}
                  >
                    {currentConfigLabel?.replace(/^Borrado \| /, "Borrado ") || "Sin configuración"}
                  </MDTypography>
                </Box>
              </Box>
            );
          })}

          <Box
            sx={{
              position: "sticky",
              top: MACHINE_HEADER_HEIGHT,
              left: 0,
              zIndex: 35,
              backgroundColor: "#f4f7fb",
              borderRight: "1px solid rgba(52, 71, 103, 0.16)",
              borderBottom: "1px solid rgba(0,0,0,0.08)",
              minHeight: VALUE_HEADER_HEIGHT,
              height: VALUE_HEADER_HEIGHT,
              px: 1.5,
              py: 0.75,
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          />

          {sortedMachines.map((machine, machineIndex) => {
            const machineColumnKey = getMachineColumnKey(machine);
            const isColumnHovered = hoveredColumnKey != null && hoveredColumnKey === machineColumnKey;

            return (
              <Box
                key={`value-header-${machine.columnKey || machine.machineId || machine.name}`}
                onMouseEnter={() => setHoveredColumnKey(machineColumnKey)}
                onMouseLeave={() => setHoveredColumnKey((current) => (current === machineColumnKey ? null : current))}
                sx={{
                  position: "sticky",
                  top: MACHINE_HEADER_HEIGHT,
                  zIndex: 25,
                  minHeight: VALUE_HEADER_HEIGHT,
                  height: VALUE_HEADER_HEIGHT,
                  backgroundColor: getColumnBackground(machineIndex, true, machine.currentActive),
                  borderRight: "1px solid rgba(52, 71, 103, 0.18)",
                  borderBottom: "1px solid rgba(0,0,0,0.08)",
                  boxShadow: "inset -1px 0 0 rgba(52, 71, 103, 0.10)",
                  px: 1.25,
                  py: 0.75,
                  boxSizing: "border-box",
                  overflow: "hidden",
                  opacity: machine.currentActive ? 1 : 0.82,
                  transition: "background-color 120ms ease, box-shadow 120ms ease",
                  ...getHoverStyles({
                    isColumnHovered,
                    isIntersectionHovered: isColumnHovered,
                    mode: "header",
                  }),
                }}
              >
                <MDTypography
                  variant="caption"
                  fontWeight="medium"
                  sx={{
                    display: "block",
                    width: "100%",
                    color: "#67748e",
                    lineHeight: 1.25,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    textAlign: "center",
                  }}
                >
                  Entrada | Salida
                </MDTypography>
              </Box>
            );
          })}

          {visibleRows.map((row, rowIndex) => {
            const isRowHovered = hoveredRowKey != null && hoveredRowKey === row.rowKey;
            const isDateClickable = canNavigateToVisitDetail(row, localId);
            const dateTitle = isDateClickable
              ? "Ver detalle de recaudación"
              : row?.type === "initialValues"
                ? "Valores iniciales, no es una recaudación"
                : undefined;

            return (
              <React.Fragment key={row.rowKey}>
                <Box
                  role={isDateClickable ? "button" : undefined}
                  tabIndex={isDateClickable ? 0 : undefined}
                  title={dateTitle}
                  aria-label={dateTitle}
                  onMouseEnter={() => setHoveredRowKey(row.rowKey)}
                  onMouseLeave={() => setHoveredRowKey((current) => (current === row.rowKey ? null : current))}
                  onClick={() => handleDateClick(row)}
                  onKeyDown={(event) => {
                    if (!isDateClickable) return;
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleDateClick(row);
                    }
                  }}
                  sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: 10,
                    backgroundColor: rowIndex % 2 === 0 ? GRID_BG_EVEN : GRID_BG_ODD,
                    borderRight: "1px solid rgba(0,0,0,0.08)",
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                    p: 1.1,
                    minHeight: 80,
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    transition: "background-color 120ms ease, box-shadow 120ms ease",
                    cursor: isDateClickable ? "pointer" : "default",
                    ...getHoverStyles({
                      isRowHovered,
                      isIntersectionHovered: isRowHovered,
                      mode: "date",
                    }),
                  }}
                >
                  <MDTypography
                    variant="button"
                    fontWeight="medium"
                    sx={{
                      display: "block",
                      color: "#344767",
                      lineHeight: 1.35,
                      textDecoration: isDateClickable ? "underline" : "none",
                      textDecorationColor: isDateClickable ? "rgba(52, 71, 103, 0.35)" : "transparent",
                      textAlign: "center",
                    }}
                  >
                    {formatRowDate(row)}
                  </MDTypography>
                </Box>

                {sortedMachines.map((machine, machineIndex) => {
                  const cell = getCellForMachine(row, machine);
                  const machineColumnKey = getMachineColumnKey(machine);
                  const currentConfig = currentConfigByColumnKey.get(machineColumnKey) || null;
                  const currentConfigLabel = currentConfig?.label || null;
                  const initialCell = cell?.initialCell || (
                    machineColumnKey
                      ? initialValuesByCellKey[`${row.rowKey}::${machineColumnKey}`] || null
                      : null
                  );
                  const styles = getCellStyles(cell, row);
                  const isColumnHovered = hoveredColumnKey != null && hoveredColumnKey === machineColumnKey;
                  const isIntersectionHovered = isRowHovered && isColumnHovered;
                  return (
                    <Box
                      key={`${row.rowKey}-${machine.columnKey || machine.machineId || machine.name}`}
                      onMouseEnter={() => {
                        setHoveredRowKey(row.rowKey);
                        setHoveredColumnKey(machineColumnKey);
                      }}
                      onMouseLeave={() => {
                        setHoveredRowKey((current) => (current === row.rowKey ? null : current));
                        setHoveredColumnKey((current) => (current === machineColumnKey ? null : current));
                      }}
                      sx={{
                        p: 0.5,
                        minHeight: 80,
                        borderRight: "1px solid rgba(52, 71, 103, 0.10)",
                        borderBottom: "1px solid rgba(0,0,0,0.06)",
                        boxSizing: "border-box",
                        backgroundColor: getCellOuterBackground({
                          machineIndex,
                          isRowHovered,
                          isColumnHovered,
                          isIntersectionHovered,
                        }),
                        transition: "background-color 120ms ease",
                      }}
                    >
                      <Box
                        sx={{
                          minHeight: 80,
                          width: "100%",
                          p: cell ? 1 : 0.75,
                          borderRadius: "10px",
                          backgroundColor: styles.backgroundColor,
                          border: `1px solid ${styles.borderColor}`,
                          boxSizing: "border-box",
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "box-shadow 120ms ease, transform 120ms ease",
                          ...getHoverStyles({
                            isRowHovered,
                            isColumnHovered,
                            isIntersectionHovered,
                          }),
                        }}
                      >
                        <CellContent
                          cell={cell}
                          row={row}
                          initialCell={initialCell}
                          currentConfigLabel={currentConfigLabel}
                          currentConfigComparisonLabel={currentConfig?.comparisonLabel || null}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

CellContent.propTypes = {
  cell: PropTypes.shape({
    state: PropTypes.string,
    hasReset: PropTypes.bool,
    creditsIn: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    creditsOut: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    netCreditsIn: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    netCreditsOut: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    moneyIn: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    moneyOut: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    netMoneyIn: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    netMoneyOut: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    postResetStartIn: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    postResetStartOut: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    valueMode: PropTypes.string,
    formula: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        type: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ]),
    isAccumulative: PropTypes.bool,
    label: PropTypes.string,
    initialCell: PropTypes.object,
  }),
  initialCell: PropTypes.shape({
    state: PropTypes.string,
    postResetStartIn: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    postResetStartOut: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    valueMode: PropTypes.string,
  }),
  row: PropTypes.shape({
    type: PropTypes.string,
  }).isRequired,
  currentConfigLabel: PropTypes.string,
  currentConfigComparisonLabel: PropTypes.string,
};

SlotAccountingGrid.propTypes = {
  initialScroll: PropTypes.shape({
    scrollTop: PropTypes.number,
    scrollLeft: PropTypes.number,
  }),
  localId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  viewState: PropTypes.shape({
    filters: PropTypes.object,
    showMachines: PropTypes.bool,
    showDebugJson: PropTypes.bool,
  }),
  activeLocalMachines: PropTypes.arrayOf(
    PropTypes.shape({
      Id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      LocalMachineId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      MachineId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      InstallationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      FormulaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      FormulaType: PropTypes.string,
      FormulaValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      FormulaName: PropTypes.string,
      IsAccumulative: PropTypes.bool,
      valueMode: PropTypes.string,
    })
  ),
  machines: PropTypes.arrayOf(
    PropTypes.shape({
      columnKey: PropTypes.string,
      machineId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      localDisplayNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      LocalDisplayNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      currentLocalDisplayNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      localDisplayNumberHistory: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      ),
      serialNumber: PropTypes.string,
      currentActive: PropTypes.bool,
      installationStartDateTime: PropTypes.string,
      installationFinishDateTime: PropTypes.string,
    })
  ).isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      rowKey: PropTypes.string.isRequired,
      type: PropTypes.string,
      visitId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      dateTime: PropTypes.string,
      dateLabel: PropTypes.string,
      cells: PropTypes.object,
    })
  ).isRequired,
};

export default SlotAccountingGrid;
